import React, { useEffect, useRef, useState } from "react";

/**
 * Hook public — récupère l'état de la promo globale (sans auth) et le
 * refresh toutes les 30 s. Utilisé par la bannière + le prix barré.
 * Toute l'app passager peut l'appeler, ça fait un seul fetch dédupé.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 30_000;

let _cached = null;
let _subs = [];
let _timer = null;

async function _fetchOnce() {
  try {
    const r = await fetch(`${BACKEND_URL}/api/price/promo/active`, { cache: "no-store" });
    if (!r.ok) return;
    const d = await r.json();
    _cached = d;
    _subs.forEach((cb) => { try { cb(d); } catch (_e) { /* ignore */ } });
  } catch (_e) { /* offline */ }
}

function _subscribe(cb) {
  _subs.push(cb);
  if (_cached) cb(_cached);
  if (!_timer) {
    _fetchOnce();
    _timer = setInterval(_fetchOnce, POLL_MS);
  }
  return () => {
    _subs = _subs.filter((f) => f !== cb);
    if (_subs.length === 0 && _timer) {
      clearInterval(_timer);
      _timer = null;
    }
  };
}

export function useGlobalPromo() {
  const [promo, setPromo] = useState(_cached);
  useEffect(() => _subscribe(setPromo), []);
  return promo;
}

/**
 * Bannière rouge affichée en haut du ClientDashboard tant que la promo
 * est active. Pulse légèrement pour attirer l'œil, dispo compacte.
 */
export function PromoBanner() {
  const promo = useGlobalPromo();
  if (!promo?.active || !promo.percent_off) return null;
  return (
    <div
      className="rounded-2xl bg-gradient-to-r from-primary to-red-600 text-white px-4 py-2.5 street-border-thin street-shadow-sm flex items-center gap-3"
      data-testid="passenger-promo-banner"
    >
      <div className="relative">
        <span className="text-2xl">🔥</span>
        <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-white animate-ping" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-[Outfit] font-black uppercase tracking-widest opacity-80">
          Promo en cours
        </div>
        <div className="font-[Outfit] text-[14px] font-black leading-tight">
          -{promo.percent_off}% sur tous les trajets
        </div>
        {promo.label && (
          <div className="text-[11px] font-[Outfit] font-medium opacity-90 truncate">
            {promo.label}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Petit widget "prix barré" — affiche le prix original barré + le prix
 * promo en gras. Utilisé partout où un tarif est montré au passager.
 */
export function PromoPrice({ amountEur, className = "", testid }) {
  const promo = useGlobalPromo();
  const n = Number(amountEur);
  const safe = Number.isFinite(n) ? n : 0;
  if (!promo?.active || !promo.percent_off) {
    return (
      <span className={className} data-testid={testid}>
        {safe.toFixed(2)}€
      </span>
    );
  }
  const discounted = Math.max(0, safe * (1 - promo.percent_off / 100));
  return (
    <span className={"inline-flex items-baseline gap-1.5 " + className} data-testid={testid}>
      <span className="line-through text-foreground/50 font-[Outfit] font-bold" style={{ fontSize: "0.78em" }}>
        {safe.toFixed(2)}€
      </span>
      <span className="font-[Outfit] font-black text-primary">
        {discounted.toFixed(2)}€
      </span>
    </span>
  );
}
